import { ReactElement, use } from 'react';
import { Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import SelectField from 'shared/components/formFields/select-field';

import WanderingBoundaryPassage from 'features/wandering-boundary-settings/domain/models/wandering-boundary-passage';
import useWanderingBoundaryPassageRowValidation from 'features/wandering-boundary-settings/hooks/wandering-boundary-passage-row-validation-hook';
import WanderingBoundaryPassageRowProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-passage-row-provider-context';
import WanderingBoundarySettingsProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-provider-context';
import styles from './wandering-boundary-select.module.scss';

interface Props {
  fieldName: Path<WanderingBoundaryPassage>;
}

const WanderingBoundarySelect = ({ fieldName }: Readonly<Props>): ReactElement => {
  const { t } = useTranslation('wanderingBoundarySettings');
  const { wanderingBoundaryPassage, form } = use(WanderingBoundaryPassageRowProviderContext);
  const { validationRules } = useWanderingBoundaryPassageRowValidation(wanderingBoundaryPassage);
  const {
    detailsState: { wanderingBoundaries },
  } = use(WanderingBoundarySettingsProviderContext);

  return (
    <Controller
      name={fieldName}
      control={form.control}
      rules={validationRules.wanderingBoundaryId}
      render={({ field, fieldState: { error } }) => (
        <SelectField
          {...field}
          error={!!error}
          helperText={error?.message?.toString()}
          variant="outlined"
          label={t('wanderingBoundaryPassageFields.wanderingBoundarySelectLabel')}
          value={field.value || ''}
          onChange={(event) => field.onChange(event.target.value || null)}
          className={styles.selectField}
        >
          {wanderingBoundaries
            ?.filter((boundary) => boundary.id)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((boundary) => (
              <MenuItem key={boundary.id} value={boundary.id}>
                {boundary.name}
              </MenuItem>
            ))}
        </SelectField>
      )}
    />
  );
};

export default WanderingBoundarySelect;
